/**
 * Device Unlock API Client
 * Handles all API calls to the SOE Device Unlock backend
 * Dynamically determines base URL and path based on current host
 */

import { log } from '@/lib/utils/logger';
import { isSpanishURL } from './utils/brandUtils';

/**
 * Get the current host from request headers
 * This works on both server and client side
 */
const getCurrentHost = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.host;
  }
  // Server-side: return empty, will need to be passed in
  return '';
};

/**
 * Determine the appropriate API base URL and path based on host
 */
const getApiConfig = (host?: string): { baseUrl: string; basePath: string } => {
  const currentHost = host || getCurrentHost();
  
  // Get the configured base URL (for localhost) or use current host
  let baseUrl = '';
  
  if (currentHost.includes('localhost')) {
    // Localhost: use configured URL
    baseUrl = process.env.NEXT_PUBLIC_SOE_API_BASE_URL || 'https://sita.totalwireless.com';
  } else if (currentHost.includes('ebiz.verizon.com')) {
    // Backend domain
    baseUrl = `https://${currentHost}`;
  } else {
    // Client domain (sita, sit1, test, www)
    baseUrl = `https://${currentHost}`;
  }
  
  // Determine path: backend uses /soe/api, client uses /nsa/soe
  const basePath = (baseUrl.includes('ebiz.verizon.com') || currentHost.includes('ebiz.verizon.com'))
    ? '/soe/api/device-unlock/digital'
    : '/nsa/soe/device-unlock/digital';
  
  return {
    baseUrl,
    basePath
  };
};

const CHANNEL_ID = process.env.NEXT_PUBLIC_CHANNEL_ID || 'DOTCOM';
const API_KEY = process.env.NEXT_PUBLIC_SOE_API_KEY || '';

/**
 * Common headers for all API requests
 */
const getHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'channelId': CHANNEL_ID,
  'x-soe-apikey': API_KEY,
  'accept-language': isSpanishURL() ? 'SPA' : 'ENG',
});

/**
 * Build full API URL with dynamic host detection
 */
const buildUrl = (endpoint: string, host?: string): string => {
  const { baseUrl, basePath } = getApiConfig(host);
  return `${baseUrl}${basePath}/${endpoint}`;
};

/**
 * Generic GET request handler
 */
const get = async <T>(endpoint: string): Promise<T> => {
  const url = buildUrl(endpoint);
  log.debug(`API GET: ${url}`);
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

/**
 * Generic POST request handler
 */
const post = async <T>(endpoint: string, body?: unknown): Promise<T> => {
  const url = buildUrl(endpoint);
  log.debug(`API POST: ${url}`, body);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

/**
 * Device Unlock API endpoints
 */
export const deviceUnlockApi = {
  // Home page content
  getHomeContent: () => get('home-content'),
  
  // Device status endpoints
  getDeviceStatus: () => get('device-status'),
  
  // Order summary - POST endpoint with user data
  getOrderSummary: (data?: unknown) => post('order-summary', data),
  getDeviceUnlockOrderSummary: () => get('order-summary'),

  
  // Status pages
  getPhoneNotActivated: () => get('phone-not-activated'),
  getDevicePending: () => get('device-pending'),
  getLockedPhone: () => get('locked-phone'),
  getUnlocked: () => get('unlocked'),
  getIneligible: () => get('ineligible'),
  getManualUnlocking: () => get('manual-unlocking'),
  getDelayed: () => get('delayed'),
};

export default deviceUnlockApi;
